import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { LeadsService } from "../../../services/user/leads.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";
import { getButtonStyles } from "../../../utility";

const LeadsComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [leadToDelete, setLeadToDelete] = useState(null);
    const rowsPerPage = 5;
    const [selectedStatusCard, setSelectedStatusCard] = useState(null);
    const [leadsTitle, setLeadsTitle] = useState('Leads');
    const [leadsTitleColor, setLeadsTitleColor] = useState('')

    // Fetch data
    useEffect(() => {
        const fetchLeadData = async () => {
            try {
                setLoading(true);
                const response = await LeadsService.getAllLeads(selectedStore);
                setData(response);
            } catch (error) {
                handleNotify("Error fetching leads", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchLeadData();
    }, [selectedStore]);

    const filterData = (data) => {
        return (
            data.filter(row =>
                (selectedStatusCard ? row.status?.statusName === selectedStatusCard : true) &&
                (
                    row.leadDate?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.companyName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.customerName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.address?.city.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.phoneNumber.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.user?.username?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.probability?.probabilityName.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.items?.item?.itemName.toLowerCase().includes(searchText.toLowerCase()) ||
                    (row.status?.statusName?.toLowerCase().includes(searchText.toLowerCase()))
                )
            )
        )
    };

    // Handle pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const filteredData = filterData(data);
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Handle Edit
    const handleEditClick = (item) => {
        navigate('/leads/edit', { state: { leadId: item?.leadId } });
    };

    // Handle Delete
    const handleDeleteClick = (lead) => {
        setLeadToDelete(lead);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (leadToDelete) {
            try {
                await LeadsService.deleteLead(leadToDelete.leadId);

                const newData = data.filter(lead => lead.leadId !== leadToDelete.leadId);
                setData(newData);

                const newFilteredData = filterData(newData);

                const newTotalPages = Math.ceil(newFilteredData.length / rowsPerPage);

                if (newFilteredData.length % rowsPerPage === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else if (currentPage > newTotalPages && newTotalPages > 0) {
                    setCurrentPage(newTotalPages);
                }

                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || "Error while deleting quote.", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };

    const handleAddNewClick = () => {
        navigate('/leads/create');
    };

    // Columns for DataTable
    const columns = [
        {
            width: '15%',
            name: 'Lead Date',
            selector: row => row.leadDate || '',
            sortable: true
        },
        {
            name: "Company Name",
            selector: (row) => row.companyName,
            sortable: true,
            width: '15%',
        },
        {
            name: "City",
            selector: (row) => row.address?.city,
            sortable: true,
            width: '15%',
        },
        {
            name: "Phone",
            selector: (row) => row.phoneNumber,
            sortable: true,
            width: '15%',
        },
        {
            name: "Products",
            // selector: (row) => row.items[0]?.item?.itemName,
            selector: (row) => row.items?.map((i) => i.item?.itemName).join(", "),
            sortable: true,
            width: '15%',
        },
        {
            name: "Competition",
            selector: (row) => row.competition,
            sortable: true,
            width: '15%',
        },
        {
            name: "Status",
            selector: (row) => row.status?.statusName,
            sortable: true,
            width: '15%',
            cell: (row) => {
                const { backgroundColor, textColor } = getButtonStyles(row.status);

                return (
                    <div className="d-flex align-items-center justify-content-between">
                        <button
                            className="btn btn-sm"
                            style={{
                                padding: "6px 12px",
                                fontSize: "14px",
                                borderRadius: "25px",
                                backgroundColor: backgroundColor,
                                color: textColor,
                            }}
                        >
                            {row.status?.statusName}
                        </button>
                    </div>
                );
            },
        },
        {
            name: "Probability",
            selector: (row) => row.probability?.probabilityName,
            sortable: true,
            width: '15%',
        },
        {
            name: "Assigned To",
            selector: (row) => `${row.user?.username}(${row.user?.role?.roleName})`,
            sortable: true,
            width: '15%',
        },
        {
            name: "Value",
            selector: (row) => row.leadValue,
            sortable: true,
            width: '10%',
        },
        {
            name: "Actions",
            button: true,
            width: '12%',
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
                        Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    const handleClickStatusCard = (statusName) => {
        setSelectedStatusCard(statusName);
        setLeadsTitle(`${statusName} Leads`);

        const status = data.find(lead => lead.status?.statusName === statusName);
        const { backgroundColor } = getButtonStyles({ colorId: status?.status?.colorId });

        if (backgroundColor) {
            setLeadsTitleColor(backgroundColor);
        } else {
            console.log("Background color not found for status:", statusName);
        }
    }

    useEffect(() => {
        const fetchLeadData = async () => {
            try {
                setLoading(true);
                const response = await LeadsService.getAllLeads(selectedStore);
                setData(response);
            } catch (error) {
                console.error("Error fetching leads:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeadData();
    }, [selectedStore]);

    // Calculate leads data by status
    const leadsByStatus = data.reduce((acc, lead) => {
        const { status } = lead;
        if (!acc[status?.statusName]) {
            acc[status?.statusName] = { count: 0, value: 0 };
        }
        acc[status?.statusName].count += 1;
        acc[status?.statusName].value += lead.leadValue;
        return acc;
    }, {});

    // Calculate the total number of leads and total value
    const totalLeads = data.length;
    // const totalValue = data.reduce((acc, lead) => acc + lead.leadValue, 0);

    return (
        <>
            {/* <!--start page wrapper --> */}
            <div className="page-wrapper m-0">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4" style={{ color: leadsTitleColor }}>
                            {leadsTitle}
                        </div>
                        <div className="ms-auto">
                            <button className="btn btn-primary" onClick={handleAddNewClick}>
                                New +
                            </button>
                        </div>
                    </div>

                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3 mt-1" >
                        {Object.keys(leadsByStatus).map((statusName) => {
                            const { count, value } = leadsByStatus[statusName];
                            const percentage = ((count / totalLeads) * 100).toFixed(2);
                            const { backgroundColor } = getButtonStyles({
                                colorId: data.find(lead => lead.status.statusName === statusName)?.status.colorId
                            });

                            return (
                                <div key={statusName} className="col-12 col-sm-6 col-lg-2"
                                    onClick={() => handleClickStatusCard(statusName)}>
                                    <div className="card radius-10" style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                        <div className="card-body">

                                            <h5 className="d-flex" style={{ color: backgroundColor }}>
                                                {statusName}
                                            </h5>
                                            <h5 className="d-flex">₹ {value.toFixed(2)}</h5>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <p className="mb-0 font-13 text-primary">{count} Leads</p>
                                                <p className="mb-0 font-13 text-primary">{percentage}%</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>
                {/* Leads Grid */}
                <div className="page-wrapper m-0">
                    <div className="page-content">
                        <div className="card radius-10 border-top border-0 border-4 border-primary mt-3 p-4">
                            <div className="card-body">
                                <div className="d-flex flex-column flex-md-row mb-3">
                                    <div className="btn-group mb-3 mb-md-0 me-2">
                                        <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                        <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                        <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                        <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                                    </div>
                                    <div className="d-flex ms-auto align-items-center">
                                        <div className="me-2">
                                            <span className="fw-bold">Search:</span>
                                        </div>
                                        <input
                                            type="text"
                                            className="form-control w-100 w-md-auto"
                                            placeholder="Search 🔍..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={currentData}
                                        fixedHeader
                                        responsive
                                        highlightOnHover
                                        sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                        progressPending={loading}
                                    />
                                </div>
                                {showModal && (
                                    <DeleteModelPopupComponent
                                        show={showModal}
                                        onClose={() => setShowModal(false)}
                                        onConfirm={handleDeleteConfirm}
                                        title="Confirm"
                                        confirmText="Delete"
                                        Message="Are you sure you want to delete this lead?"
                                    />
                                )}
                                {/* Pagination Controls */}
                                <div className="d-flex mt-4 justify-content-end">
                                    <div className="btn-group">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Prev
                                        </button>
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <button
                                                key={index}
                                                className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => handlePageChange(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        ))}
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default LeadsComponent;